import { browserHistory } from '../helpers/components';
import _get from 'lodash/get';

import { MEMBER_STATUS_APPLIED } from '../constants/backend';

import { safe, gtmPush, sessionStorageGetJson, getPrevAction } from '../utils/utils';

import { STORAGE_KEY } from '../constants/generic';
import {
  getIncompleteUserStatus,
  getActionError,
  getAppliedUserStatus
} from '../helpers/selectors';
import { SERVER_ERROR } from '../helpers/errors';
import { addActionError } from '../utils/flashMessage/creator';
import { normalizePublicCompaniesResponse } from '../helpers/ssoRegistrationFlow';
import {
  clearGdprNoticeDismissal,
  gdprNoticeDismissalOutlived,
  readGdprNoticeDismissal,
  saveGdprNoticeDismissal
} from '../helpers/gdprNotice';

import {
  clearAuthTokens,
  clearSsoTokens,
  parseAuthTokensFromApi,
  saveAuthTokens,
  saveSsoTokens
} from '../utils/tokens';

function parseAuthTokens(action) {
  if (action.type === 'keycloak/VERIFY_USER_SUCCESS') {
    saveSsoTokens();
  } else {
    const tokens = parseAuthTokensFromApi(action?.payload?.headers);
    const meTokens = parseAuthTokensFromApi(action?.payload?.config?.headers);

    saveAuthTokens(tokens.token ? tokens : meTokens);
  }
}

function clearDataOnLogout(state) {
  sessionStorage.removeItem(STORAGE_KEY.USER);
  clearAuthTokens();
  clearSsoTokens();

  return {
    ...forgetGdprNoticeDismissal(state),
    data: null,
    isRegisterIncompleteUser: false,
    profiles: null
  };
}

function forgetGdprNoticeDismissal(state) {
  clearGdprNoticeDismissal();

  return { ...state, gdprNoticeDismissal: null };
}

function getRegisterIncompleteUserStatus(userData) {
  return getIncompleteUserStatus(userData) || getAppliedUserStatus(userData);
}

// A member returned by /me, taken into the slice together with the "his registration
// is still unfinished" reading of him, so nothing downstream can catch one without
// the other. /me is deliberately the only post-Keycloak source of member data: the
// registration endpoint can describe a member that the service then does not return
// on read, which is the inconsistency that exposed this flow.
//
// A refresh of the member already in the slice deliberately does NOT come through here:
// user/GET_INFO_SUCCESS and user/UPDATE_INFO_SUCCESS assign `data` on their own. Every
// Creation step saves through UPDATE_INFO, and re-reading the flag there would move it
// mid-registration: the heading and final button are chosen from it and may not change
// between two steps. The /me PersonalInfo fires immediately after registration is the
// exception because it is the first trustworthy member answer, not a mid-flow refresh;
// verifyRegisteredUser asks to derive the flag here. Later it is refreshed at the flow
// boundary by user/AUTO_UPDATE_REGISTER_INCOMPLETE_STATUS in Creation cleanup.
function storeMember(state, user) {
  return {
    ...state,
    data: user,
    isRegisterIncompleteUser: getRegisterIncompleteUserStatus(user)
  };
}

const fetchingProps = {
  sendEmailFetching: false,
  updateSettingsFetching: false,
  inviteCodeFetching: false,
  userDataUpdating: false,
  companyIdFetching: false,
  showSubscribeLoader: false
};

const getDefaultState = () => ({
  updateError: null,
  companyIdError: null,
  data: sessionStorageGetJson(STORAGE_KEY.USER),
  email: null,
  activationSuccess: false,
  alreadyActivated: false,
  activationExpired: false,
  sendEmailSuccess: false,
  inviteError: null,
  company: false,
  companyBrand: false,
  registerCompany: null,
  brandCompanies: null,
  registerInnerStateData: null,
  registrationStatusData: null,
  settings: null,
  profiles: null,
  isRegisterIncompleteUser: false,
  gdprNoticeDismissal: readGdprNoticeDismissal(),
  ...fetchingProps
});

export default function reducer(state = getDefaultState(), action) {
  switch (action.type) {
    case 'user/SAVE_REGISTER_INNER_STATE_DATA': {
      state = { ...state, registerInnerStateData: action.data };
      break;
    }

    case 'user/CLEAR_REGISTER_INNER_STATE_DATA': {
      state = { ...state, registerInnerStateData: null };
      break;
    }

    case 'user/SAVE_REGISTRATION_STATUS_DATA': {
      state = { ...state, registrationStatusData: { ...action.data } };
      break;
    }

    case 'user/CLEAR_REGISTRATION_STATUS_DATA': {
      state = { ...state, registrationStatusData: null };
      break;
    }

    case 'user/SET_REGISTER_INCOMPLETE_STATUS': {
      state = { ...state, isRegisterIncompleteUser: action.status };
      break;
    }

    case 'user/AUTO_UPDATE_REGISTER_INCOMPLETE_STATUS': {
      state = {
        ...state,
        isRegisterIncompleteUser: getRegisterIncompleteUserStatus(state.data)
      };
      break;
    }

    case 'user/SILENT_REFRESH_SUCCESS': {
      saveAuthTokens(parseAuthTokensFromApi(action.payload.headers));
      break;
    }

    case 'user/REFRESH_SUCCESS':
    case 'keycloak/VERIFY_USER_SUCCESS': {
      const user = action?.payload?.data;
      const { updateIncompleteStatus = true } = getPrevAction(action);

      parseAuthTokens(action);

      state = updateIncompleteStatus
        ? storeMember(state, user)
        : { ...state, data: user };

      // Every Keycloak bootstrap (login return, F5, session replaced in another tab)
      // lands here: a GDPR notice dismissal given in another Keycloak session stops
      // applying; the same session keeps it.
      if (
        action.type === 'keycloak/VERIFY_USER_SUCCESS' &&
        gdprNoticeDismissalOutlived(
          state.gdprNoticeDismissal,
          getPrevAction(action).authSession ?? null
        )
      ) {
        state = forgetGdprNoticeDismissal(state);
      }

      gtmPush(
        { event: 'Authentification', authentificationType: 'success' },
        _get(state, 'data.id')
      );

      break;
    }

    case 'user/GET_SETTINGS':
    case 'user/UPDATE_SETTINGS': {
      state = { ...state, updateSettingsFetching: true };
      break;
    }

    case 'user/UPDATE_SETTINGS_SUCCESS':
    case 'user/GET_SETTINGS_SUCCESS': {
      const settings = action.payload.data;

      state = { ...state, settings, updateSettingsFetching: false };
      break;
    }

    case 'user/UPDATE_SETTINGS_FAIL':
    case 'user/GET_SETTINGS_FAIL': {
      state = { ...state, updateSettingsFetching: false };
      break;
    }

    case 'user/LOGOUT':
    case 'user/SELF_ANONYMIZE_SUCCESS': {
      state = clearDataOnLogout(state);
      if (action.redirect) browserHistory.push('/');
      break;
    }

    case 'user/LINK_PROFILE':
    case 'user/REGISTER_SSO': {
      state = { ...state, userDataUpdating: true };
      break;
    }

    case 'user/LINK_PROFILE_SUCCESS':
    case 'user/REGISTER_SSO_SUCCESS': {
      // These writes confirm that the mutation was accepted; neither response is a
      // readable-member verdict. REGISTER_SSO may even return a member that the next
      // /me cannot find, while LINK_PROFILE answers with a profile. Only /me may fill
      // `data` and derive the incomplete-registration status after either mutation.
      state = { ...state, userDataUpdating: false };

      gtmPush({
        event: 'Registration',
        registrationType: 'success'
      });

      break;
    }

    case 'user/LINK_PROFILE_FAIL':
    case 'user/REGISTER_SSO_FAIL': {
      state = { ...state, userDataUpdating: false };
      break;
    }

    case 'user/SET_SUBSCRIBE_LOADER_STATE': {
      state = { ...state, showSubscribeLoader: action.payload };
      break;
    }

    case 'user/FINAL_ACCESS_DENIED': {
      state = clearDataOnLogout(state);
      break;
    }

    case 'user/CLEAR_CACHED_USER': {
      state = clearDataOnLogout(state);
      break;
    }

    case 'user/SEND_ACTIVATION_EMAIL': {
      state = { ...state, sendEmailFetching: true, sendEmailSuccess: false };
      break;
    }

    case 'user/SEND_ACTIVATION_EMAIL_SUCCESS': {
      state = { ...state, sendEmailSuccess: true, sendEmailFetching: false };
      break;
    }

    case 'user/SEND_ACTIVATION_EMAIL_FAIL': {
      if (typeof _get(action, 'error.response.data.developerMessage') === 'string') {
        const devMsgArray = getActionError(action).developerMessage.split(' ');
        const userActivated = _get(devMsgArray, '[3]') === 'already';
        const userNotFound = _get(devMsgArray, '[2]') === 'found';

        if (userNotFound || userActivated) state = { ...state, sendEmailSuccess: true };
      }

      state = { ...state, sendEmailFetching: false };
      break;
    }

    case 'user/ACCOUNT_ACTIVATION': {
      state = { ...state, alreadyActivated: false, activationExpired: false };
      break;
    }

    case 'user/ACCOUNT_ACTIVATION_SUCCESS': {
      state = {
        ...state,
        activationSuccess: true,
        alreadyActivated: false,
        activationExpired: false
      };
      break;
    }

    case 'user/ACCOUNT_ACTIVATION_FAIL': {
      if (_get(action, 'error.response.status') === 410) {
        state = { ...state, activationExpired: true };
      } else if (
        typeof _get(action, 'error.response.data.developerMessage') === 'string'
      ) {
        const devMsgArray = getActionError(action).developerMessage.split(' ');
        const userActivated = _get(devMsgArray, '[5]') === 'status';
        const status = _get(devMsgArray, '[6]');

        if (userActivated && status !== MEMBER_STATUS_APPLIED)
          state = { ...state, alreadyActivated: true };
      } else {
        addActionError(action);
      }

      break;
    }

    case 'user/REMOVE_UPDATE_ERRORS': {
      state = { ...state, updateError: null };
      break;
    }

    case 'user/GET_INFO_SUCCESS': {
      let user = { ...action.payload.data };

      state = { ...state, data: user };
      break;
    }

    case 'user/GET_INFO_FAIL': {
      const { silent } = getPrevAction(action);

      if (!silent) addActionError(action);
      break;
    }

    case 'user/UPDATE_INFO': {
      state = { ...state, userDataUpdating: true };
      break;
    }

    case 'user/UPDATE_INFO_SUCCESS': {
      const user = action.payload.data;

      state = { ...state, data: user, userDataUpdating: false };
      break;
    }

    case 'user/UPDATE_INFO_FAIL': {
      state = {
        ...state,
        userDataUpdating: false
      };


      break;
    }

    case 'user/GET_FILE_INFO_SUCCESS': {
      break;
    }

    case 'user/GET_COMPANY_ID': {
      state = { ...state, companyIdFetching: true, companyIdError: null };
      break;
    }

    case 'user/GET_COMPANY_ID_FAIL': {
      state = { ...state, companyIdFetching: false, companyIdError: true };
      break;
    }

    case 'user/GET_COMPANY_ID_SUCCESS': {
      state = { ...state, companyIdFetching: false };
      break;
    }

    case 'user/GET_COMPANY_SUCCESS': {
      state = { ...state, company: action.payload.data };
      break;
    }

    case 'user/CHECK_INVITE_CODE': {
      state = { ...state, inviteCodeFetching: true, inviteError: null };
      break;
    }

    case 'user/CHECK_INVITE_CODE_FAIL': {
      // there is no body to read on a network failure or an empty 5xx: report the
      // transport problem instead of throwing and leaving the form spinning
      const inviteError = safe(() => action.error.response.data) || SERVER_ERROR;

      state = {
        ...state,
        inviteError,
        inviteCodeFetching: false
      };

      break;
    }

    case 'user/CHECK_INVITE_CODE_ERROR': {
      state = {
        ...state,
        inviteError: action.error,
        inviteCodeFetching: false
      };
      break;
    }

    case 'user/CHECK_INVITE_CODE_SUCCESS': {
      const { keepFetching } = getPrevAction(action);

      state = { ...state, inviteCodeFetching: keepFetching };
      break;
    }

    case 'user/CHECK_INVITE_CODE_FETCH_DONE': {
      state = { ...state, inviteCodeFetching: false };
      break;
    }

    case 'user/CHECK_INVITE_CODE_CLEAN_ERROR': {
      state = {
        ...state,
        inviteError: null
      };
      break;
    }

    case 'user/GET_REGISTER_COMPANY_SUCCESS': {
      state = { ...state, registerCompany: action.payload.data };
      break;
    }

    case 'user/GET_BRAND_COMPANIES': {
      state = { ...state, brandCompanies: null };
      break;
    }

    case 'user/GET_BRAND_COMPANIES_SUCCESS': {
      // The endpoint answers with a paginated {results} envelope, which the old
      // `data.length` read never saw - the list silently stayed null. A successful
      // empty response stores [] so "no companies" stays tellable from "not loaded"
      // (null); an unrecognised shape stays null for the same reason.
      state = {
        ...state,
        brandCompanies: normalizePublicCompaniesResponse(action.payload.data)
      };
      break;
    }

    case 'user/GET_BRAND_COMPANIES_FAIL': {
      // Surface the error; brandCompanies stays null ("not loaded") - a failed
      // request is never presented as a brand with zero companies.
      addActionError(action);
      break;
    }

    case 'user/GET_REGISTER_COMPANY_FAIL': {
      addActionError(action);
      break;
    }

    case 'user/GET_COMPANY_FOR_BRAND_SUCCESS': {
      state = { ...state, companyBrand: action.payload.data };
      break;
    }

    case 'user/CLEAR_REGISTER_COMPANY_DATA': {
      state = { ...state, registerCompany: null };
      break;
    }

    case 'user/GET_PROFILES_SUCCESS': {
      state = { ...state, profiles: action.payload.data };
      break;
    }

    case 'user/GET_PROFILES_FAIL': {
      addActionError(action);
      state = { ...state, profiles: null };
      break;
    }

    case 'user/DISMISS_GDPR_NOTICE': {
      const dismissal = { profileId: action.profileId, session: action.session };

      saveGdprNoticeDismissal(dismissal);
      state = { ...state, gdprNoticeDismissal: dismissal };
      break;
    }

    // Another login, or another active profile, sees the notice again.
    case 'user/RESET_GDPR_NOTICE_DISMISSAL':
    case 'user/SWITCH_PROFILE_SUCCESS': {
      state = forgetGdprNoticeDismissal(state);
      break;
    }

    default: {
      break;
    }
  }

  return state;
}
